import React, { useEffect, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api, { formatMoney } from "@/lib/api";
import { numeroALetras, parseMoney } from "@/lib/numeroALetras";

const VARS = [
    { key: "fecha_hoy", label: "Fecha de firma" },
    { key: "nombre", label: "Cliente" },
    { key: "ci_ruc", label: "CI / RUC" },
    { key: "fecha_evento", label: "Fecha del evento" },
    { key: "invitados", label: "Invitados" },
    { key: "valor", label: "Valor total (USD)" },
    { key: "valor_letras", label: "Valor en letras", wide: true },
    { key: "abono", label: "Abono / anticipo (USD)" },
    { key: "abono_letras", label: "Abono en letras", wide: true },
    { key: "saldo", label: "Saldo pendiente (USD)" },
    { key: "precio_extra_pax", label: "$ por invitado extra" },
    { key: "estructura", label: "Estructura (SI / NO)" },
    { key: "m2_estructura", label: "m² de estructura" },
    { key: "carpas_servicio", label: "Carpas de servicio" },
    { key: "m2_piso", label: "m² piso flotante" },
];

const EMPTY_VARS = Object.fromEntries(VARS.map((v) => [v.key, ""]));
const CLAUSE_PLACEHOLDER = "{{clausulas_adicionales}}";

export default function AdminContratos() {
    const [leads, setLeads] = useState([]);
    const [tpl, setTpl] = useState("");
    const [vars, setVars] = useState({ ...EMPTY_VARS, fecha_hoy: new Date().toLocaleDateString("es-EC") });
    const [selLead, setSelLead] = useState(null);
    const [email, setEmail] = useState("");
    const [sending, setSending] = useState(false);
    const [pct, setPct] = useState("30");
    const [clauses, setClauses] = useState([]);
    const [selectedClauses, setSelectedClauses] = useState({});
    const [newClause, setNewClause] = useState({ title: "", text: "" });

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/leads");
            setLeads(data.filter((l) => l.status !== "borrador"));
        } catch (err) { /* noop */ }
        try { const { data: t } = await api.get("/admin/contract-template"); setTpl(t.body || ""); }
        catch (err) { /* noop */ }
        try { const { data: c } = await api.get("/admin/contract-clauses"); setClauses(c); }
        catch (err) { /* noop */ }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setVar = (key, value) => {
        const next = { ...vars, [key]: value };
        if (key === "valor") {
            const v = parseMoney(value);
            next.valor_letras = v > 0 ? numeroALetras(v) : "";
            const a = parseMoney(next.abono);
            if (a > 0) next.saldo = formatMoney(Math.max(0, v - a));
        }
        if (key === "abono") {
            const a = parseMoney(value);
            next.abono_letras = a > 0 ? numeroALetras(a) : "";
            const v = parseMoney(next.valor);
            if (v > 0) next.saldo = formatMoney(Math.max(0, v - a));
        }
        setVars(next);
    };

    const calcAnticipo = () => {
        const v = parseMoney(vars.valor);
        if (!v) { toast.error("Ingresa primero el valor total del contrato"); return; }
        const p = Number(pct) || 0;
        const abonoVal = Math.round(v * p / 100 * 100) / 100;
        setVars((prev) => ({
            ...prev,
            abono: formatMoney(abonoVal),
            abono_letras: numeroALetras(abonoVal),
            saldo: formatMoney(Math.max(0, v - abonoVal)),
        }));
        toast.success(`Anticipo del ${p}% calculado: ${formatMoney(abonoVal)}`);
    };

    const fillFromLead = (l) => {
        setSelLead(l);
        setEmail(l.email || "");
        const carpa = l.carpa || {};
        const valor = l.sale?.sale_amount || l.total || 0;
        setVars({
            ...EMPTY_VARS,
            fecha_hoy: new Date().toLocaleDateString("es-EC"),
            nombre: l.contact_name || "",
            fecha_evento: l.exact_date || "por definir",
            invitados: String(l.guests ?? ""),
            valor: formatMoney(valor),
            valor_letras: valor > 0 ? numeroALetras(valor) : "",
            precio_extra_pax: "15",
            estructura: carpa.width ? "SI" : "NO",
            m2_estructura: carpa.area_m2 ? String(carpa.area_m2) : "0",
            carpas_servicio: "0",
            m2_piso: "0",
        });
        toast.success(`Variables cargadas desde la cotización de ${l.contact_name}`);
    };

    const clausesBlock = () => {
        const active = clauses.filter((c) => selectedClauses[c.id]);
        if (active.length === 0) return "";
        const lines = active.map((c, i) => `CLÁUSULA ADICIONAL ${i + 1}. - ${c.title.toUpperCase()}: ${c.text}`);
        return `\nCLÁUSULAS ADICIONALES\n${lines.join("\n")}\n`;
    };

    const rendered = () => {
        let body = tpl;
        Object.entries(vars).forEach(([k, v]) => { body = body.replaceAll(`{{${k}}}`, v || "________"); });
        const block = clausesBlock();
        if (body.includes(CLAUSE_PLACEHOLDER)) body = body.replaceAll(CLAUSE_PLACEHOLDER, block);
        else if (block) body = `${body}\n${block}`;
        return body;
    };

    const generate = () => {
        const w = window.open("", "_blank");
        w.document.write(`<html><head><title>Acuerdo — ${vars.nombre || "Contrato"}</title>
            <style>body{font-family:Georgia,serif;max-width:760px;margin:40px auto;color:#1b2a3d;line-height:1.65;white-space:pre-wrap;font-size:12.5px;padding:0 20px}</style>
            </head><body>${rendered().replaceAll("\n", "<br/>")}</body></html>`);
        w.document.close();
        setTimeout(() => w.print(), 500);
    };

    const sendByEmail = async () => {
        if (!email) { toast.error("Ingresa el email del cliente"); return; }
        setSending(true);
        try {
            let leadId = selLead?.id;
            if (!leadId) {
                const match = leads.find((l) => (l.email || "").toLowerCase() === email.toLowerCase());
                leadId = match?.id;
            }
            if (!leadId) { toast.error("Selecciona primero una cotización para asociar el envío"); setSending(false); return; }
            const { data } = await api.post(`/admin/leads/${leadId}/send-contract-email`, {
                email,
                subject: `Contrato para firma — ${vars.nombre || "Hacienda Nápoles"}`,
                body_text: rendered(),
            });
            toast.success(data.delivery?.sent ? "Contrato enviado por email ✉️" : "Registrado (Resend inactivo: modo log-only)");
        } catch (_e) { toast.error("Error al enviar el contrato"); }
        finally { setSending(false); }
    };

    const saveTemplate = async () => {
        try { await api.put("/admin/contract-template", { body: tpl }); toast.success("Plantilla guardada"); }
        catch (_e) { toast.error("Error al guardar plantilla"); }
    };

    // ---- Cláusulas administrables ----
    const addClause = async () => {
        if (!newClause.title || !newClause.text) { toast.error("Título y texto de la cláusula requeridos"); return; }
        try {
            const { data } = await api.post("/admin/contract-clauses", { ...newClause, order: clauses.length + 1 });
            setClauses([...clauses, data]);
            setNewClause({ title: "", text: "" });
            toast.success("Cláusula agregada");
        } catch (_e) { toast.error("Error al agregar cláusula"); }
    };
    const saveClause = async (c) => {
        try {
            await api.put(`/admin/contract-clauses/${c.id}`, { title: c.title, text: c.text, order: c.order });
            toast.success("Cláusula guardada");
        } catch (_e) { toast.error("Error al guardar cláusula"); }
    };
    const deleteClause = async (id) => {
        if (!window.confirm("¿Eliminar esta cláusula del catálogo?")) return;
        try {
            await api.delete(`/admin/contract-clauses/${id}`);
            setClauses(clauses.filter((c) => c.id !== id));
            setSelectedClauses((prev) => { const n = { ...prev }; delete n[id]; return n; });
            toast.success("Cláusula eliminada");
        } catch (_e) { toast.error("Error al eliminar"); }
    };
    const updateClauseLocal = (id, patch) => setClauses(clauses.map((c) => c.id === id ? { ...c, ...patch } : c));

    return (
        <div className="space-y-4" data-testid="admin-contratos">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {/* Modo A: desde cotización */}
                <div className="bg-white border border-border p-5 max-h-[560px] overflow-y-auto">
                    <h3 className="font-display text-2xl mb-1">Desde una cotización</h3>
                    <p className="text-[11px] text-muted-foreground mb-3">Selecciona una cotización y la plantilla se llena automáticamente.</p>
                    {leads.map((l) => (
                        <button
                            key={l.id}
                            type="button"
                            onClick={() => fillFromLead(l)}
                            className={`w-full text-left border-b border-border py-2 px-2 text-xs hover:bg-secondary/50 ${selLead?.id === l.id ? "bg-secondary" : ""}`}
                            data-testid={`contract-lead-${l.id}`}
                        >
                            <div className="flex justify-between">
                                <span className="font-medium">{l.contact_name}</span>
                                <span className="tabular text-muted-foreground">{l.total != null ? formatMoney(l.total) : "—"}</span>
                            </div>
                            <div className="text-muted-foreground">{l.event_type || "Boda"} · {l.exact_date || "sin fecha"}</div>
                        </button>
                    ))}
                    {leads.length === 0 && <div className="overline text-muted-foreground">Sin cotizaciones</div>}
                </div>

                {/* Modo B: variables manuales */}
                <div className="lg:col-span-2 bg-white border border-border p-6">
                    <h3 className="font-display text-2xl mb-1">Variables del contrato</h3>
                    <p className="text-[11px] text-muted-foreground mb-4">Edítalas libremente o genera un contrato desde cero sin cotización. El valor en letras se genera automáticamente y puedes ajustarlo.</p>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="contract-vars-form">
                        {VARS.map((v) => (
                            <div key={v.key} className={v.wide ? "col-span-2 md:col-span-3" : ""}>
                                <Label className="overline text-muted-foreground text-[10px]">{v.label}</Label>
                                <Input
                                    value={vars[v.key] || ""}
                                    onChange={(e) => setVar(v.key, e.target.value)}
                                    className="rounded-none text-xs mt-1"
                                    data-testid={`var-${v.key}`}
                                />
                            </div>
                        ))}
                    </div>

                    {/* Anticipo automático */}
                    <div className="flex flex-wrap items-end gap-3 mt-4 bg-secondary/40 border border-border p-3" data-testid="anticipo-calc">
                        <div>
                            <Label className="overline text-muted-foreground text-[10px]">% de anticipo</Label>
                            <Input type="number" value={pct} onChange={(e) => setPct(e.target.value)} className="rounded-none text-xs mt-1 w-24 tabular" data-testid="input-pct-anticipo" />
                        </div>
                        <Button variant="outline" onClick={calcAnticipo} className="rounded-none" data-testid="btn-calc-anticipo">
                            Calcular anticipo ({Number(pct) || 0}%)
                        </Button>
                        <p className="text-[10px] text-muted-foreground max-w-xs">Calcula el abono sobre el valor total. El monto y el porcentaje son editables si negocian condiciones distintas.</p>
                    </div>

                    <div className="flex flex-wrap items-end gap-3 mt-5 border-t border-border pt-4">
                        <Button onClick={generate} className="rounded-none bg-primary text-primary-foreground" data-testid="btn-generate-contract-full">
                            📄 Generar contrato (imprimir / PDF)
                        </Button>
                        <div className="flex items-end gap-2">
                            <div>
                                <Label className="overline text-muted-foreground text-[10px]">Email del cliente</Label>
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="cliente@email.com" className="rounded-none text-xs mt-1 w-56" data-testid="contract-email" />
                            </div>
                            <Button onClick={sendByEmail} disabled={sending} variant="outline" className="rounded-none" data-testid="btn-send-contract-email">
                                ✉️ Enviar por email para firma
                            </Button>
                        </div>
                        <Button variant="ghost" onClick={() => { setSelLead(null); setVars({ ...EMPTY_VARS, fecha_hoy: new Date().toLocaleDateString("es-EC") }); setSelectedClauses({}); }} className="rounded-none text-muted-foreground" data-testid="btn-clear-vars">
                            Limpiar
                        </Button>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">El envío por email queda registrado en el lead. Con Resend inactivo, el contenido se registra en logs (modo log-only).</p>
                </div>
            </div>

            {/* Cláusulas adicionales Sí/No */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2 bg-white border border-border p-6" data-testid="contract-clauses-section">
                    <h3 className="font-display text-2xl mb-1">Cláusulas adicionales</h3>
                    <p className="text-[11px] text-muted-foreground mb-4">
                        Marca <strong>Sí</strong> en las cláusulas que apliquen a este contrato: se insertan automáticamente al generarlo.
                        Puedes editar el texto de cada cláusula o crear nuevas sin rehacer el contrato.
                        {" "}Si la plantilla contiene <code className="bg-secondary px-1">{"{{clausulas_adicionales}}"}</code> se insertan en esa posición; si no, se agregan al final.
                    </p>
                    {clauses.length === 0 && <div className="overline text-muted-foreground">Sin cláusulas en el catálogo — crea la primera a la derecha.</div>}
                    <div className="space-y-3">
                        {clauses.map((c) => {
                            const on = !!selectedClauses[c.id];
                            return (
                                <div key={c.id} className={`border p-3 ${on ? "border-primary bg-primary/5" : "border-border"}`} data-testid={`clause-${c.id}`}>
                                    <div className="flex items-center gap-3 flex-wrap">
                                        <div className="flex border border-border">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedClauses({ ...selectedClauses, [c.id]: true })}
                                                className={`px-3 py-1 text-[10px] overline ${on ? "bg-primary text-primary-foreground" : "bg-white text-muted-foreground"}`}
                                                data-testid={`clause-yes-${c.id}`}
                                            >Sí</button>
                                            <button
                                                type="button"
                                                onClick={() => setSelectedClauses({ ...selectedClauses, [c.id]: false })}
                                                className={`px-3 py-1 text-[10px] overline ${!on ? "bg-foreground text-background" : "bg-white text-muted-foreground"}`}
                                                data-testid={`clause-no-${c.id}`}
                                            >No</button>
                                        </div>
                                        <Input
                                            value={c.title}
                                            onChange={(e) => updateClauseLocal(c.id, { title: e.target.value })}
                                            className="rounded-none text-xs font-medium flex-1 min-w-[180px]"
                                            data-testid={`clause-title-${c.id}`}
                                        />
                                        <Button size="sm" variant="ghost" onClick={() => saveClause(clauses.find((x) => x.id === c.id))} className="rounded-none text-xs" data-testid={`btn-save-clause-${c.id}`}>Guardar</Button>
                                        <Button size="sm" variant="ghost" onClick={() => deleteClause(c.id)} className="rounded-none text-destructive text-xs" data-testid={`btn-del-clause-${c.id}`}>Eliminar</Button>
                                    </div>
                                    <textarea
                                        value={c.text}
                                        onChange={(e) => updateClauseLocal(c.id, { text: e.target.value })}
                                        rows={2}
                                        className="w-full border border-border rounded-none p-2 text-xs mt-2 focus:outline-none focus:border-foreground"
                                        data-testid={`clause-text-${c.id}`}
                                    />
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div className="bg-white border border-border p-6 space-y-3 h-fit">
                    <h3 className="font-display text-2xl">Nueva cláusula</h3>
                    <div>
                        <Label className="overline text-muted-foreground text-[10px]">Título (ej: PISO FLOTANTE ADICIONAL)</Label>
                        <Input value={newClause.title} onChange={(e) => setNewClause({ ...newClause, title: e.target.value })} className="rounded-none mt-1" data-testid="input-new-clause-title" />
                    </div>
                    <div>
                        <Label className="overline text-muted-foreground text-[10px]">Texto de la cláusula</Label>
                        <textarea
                            value={newClause.text}
                            onChange={(e) => setNewClause({ ...newClause, text: e.target.value })}
                            rows={5}
                            className="w-full border border-border rounded-none p-2 text-xs mt-1 focus:outline-none focus:border-foreground"
                            data-testid="input-new-clause-text"
                        />
                    </div>
                    <Button onClick={addClause} className="rounded-none bg-foreground text-background" data-testid="btn-add-clause">Agregar al catálogo</Button>
                    <p className="text-[10px] text-muted-foreground">Puedes usar variables como {"{{m2_piso}}"} o {"{{invitados}}"} dentro del texto: se reemplazan al generar.</p>
                </div>
            </div>

            {/* Plantilla */}
            <div className="bg-white border border-border p-6" data-testid="contract-template-section">
                <div className="flex items-baseline justify-between flex-wrap gap-2 mb-2">
                    <h3 className="font-display text-2xl">Plantilla estándar (Acuerdo Privado Hacienda Nápoles)</h3>
                    <span className="text-[10px] text-muted-foreground">Variables: {VARS.map((v) => `{{${v.key}}}`).join(" ")} {CLAUSE_PLACEHOLDER}</span>
                </div>
                <textarea
                    value={tpl}
                    onChange={(e) => setTpl(e.target.value)}
                    rows={16}
                    className="w-full border border-border rounded-none p-3 text-[11px] font-mono focus:outline-none focus:border-foreground"
                    data-testid="contract-template-body-full"
                />
                <Button onClick={saveTemplate} className="mt-3 rounded-none bg-foreground text-background" data-testid="btn-save-template-full">Guardar plantilla</Button>
            </div>
        </div>
    );
}
